import { useState, type ReactNode } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/** Title, optional text beside it, and actions for an AppNotes screen. */
export function PageHeader({
  actions,
  aside,
  title,
}: {
  readonly actions?: ReactNode;
  readonly aside?: ReactNode;
  readonly title: string;
}) {
  return (
    <header className="page-header">
      <h1>{title}</h1>
      {aside}
      {actions === undefined ? null : (
        <div className="page-actions">{actions}</div>
      )}
    </header>
  );
}

/** Shared framed collection surface for AppNotes console pages. */
export function ConsoleSection({
  actions,
  children,
  count,
  description,
  title,
  titleId,
}: {
  readonly actions?: ReactNode;
  readonly children: ReactNode;
  readonly count?: number | undefined;
  readonly description?: string | undefined;
  readonly title: string;
  readonly titleId: string;
}) {
  return (
    <section aria-labelledby={titleId} className="console-section">
      <header className="console-section-header">
        <div>
          <h2 id={titleId}>
            {title}
            {count === undefined ? null : (
              <span className="ledger-count"> {count}</span>
            )}
          </h2>
          {description === undefined ? null : <p>{description}</p>}
        </div>
        {actions === undefined ? null : (
          <div className="console-section-actions">{actions}</div>
        )}
      </header>
      <div className="console-section-body">{children}</div>
    </section>
  );
}

/** Intentional loading, empty, forbidden, and unavailable screen state. */
export function StatePanel({
  action,
  description,
  title,
}: {
  readonly action?: ReactNode;
  readonly description: string;
  readonly title: string;
}) {
  return (
    <section className="state-panel">
      <h2>{title}</h2>
      <p>{description}</p>
      {action === undefined ? null : <div>{action}</div>}
    </section>
  );
}

/**
 * Compact product status label. A bare number is a count, which Hairline sets
 * as a plain tabular number rather than a pill; words render as a tag.
 */
export function StatusBadge({
  children,
  tone = "neutral",
}: {
  readonly children: ReactNode;
  readonly tone?: "danger" | "neutral" | "primary";
}) {
  if (typeof children === "number") {
    return <span className="ledger-count">{children}</span>;
  }
  return (
    <Badge
      className={cn(tone === "primary" && "text-foreground")}
      variant={tone === "danger" ? "destructive" : "default"}
    >
      {children}
    </Badge>
  );
}

/** Label-value metadata row that tolerates long content. */
export function MetadataRow({
  children,
  label,
}: {
  readonly children: ReactNode;
  readonly label: string;
}) {
  return (
    <div className="grid gap-1 border-b border-line py-2.5 last:border-b-0 sm:grid-cols-[10rem_minmax(0,1fr)] sm:gap-4">
      <dt className="text-[13px] text-fg-3">{label}</dt>
      <dd className="min-w-0 break-words text-[13px]">{children}</dd>
    </div>
  );
}

/** Copyable one-time credential or guest URL. */
export function SecretPanel({
  label,
  value,
}: {
  readonly label: string;
  readonly value: string;
}) {
  const [copyState, setCopyState] = useState<"idle" | "copied" | "failed">(
    "idle",
  );
  async function copy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(value);
      setCopyState("copied");
    } catch {
      setCopyState("failed");
    }
  }
  return (
    <section className="min-w-0">
      <p className="text-[13px] font-medium">{label}</p>
      <code className="mt-2 block overflow-x-auto rounded-md border border-line bg-bg-sunken px-3.5 py-2.5 font-mono text-[12.5px] leading-5 select-text">
        {value}
      </code>
      <Button
        className="mt-3"
        onClick={() => void copy()}
        size="sm"
        type="button"
        variant="outline"
      >
        {copyState === "copied" ? "Copied" : "Copy"}
      </Button>
      <p className="mt-2 text-[12.5px] text-fg-3" role="status">
        {copyState === "failed"
          ? "Clipboard unavailable. Select and copy the credential above."
          : copyState === "copied"
            ? "Copied to clipboard."
            : ""}
      </p>
    </section>
  );
}
